/**
 * Services API pour le service traiteur
 */

import { apiCall } from './api';

const CATERING_BASE_URL = '/catering';

// =============================================================================
// SERVICES TRAITEUR
// =============================================================================

/**
 * Créer un nouveau service traiteur
 */
export const createCateringService = async (serviceData) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/services`, {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la création du service traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer la liste des services traiteur
 */
export const getCateringServices = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.restaurant_id) queryParams.append('restaurant_id', params.restaurant_id);
    if (params.formula_id) queryParams.append('formula_id', params.formula_id);
    if (params.service_type) queryParams.append('service_type', params.service_type);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${CATERING_BASE_URL}/services${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiCall(url, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération des services traiteur:', error);
    throw error;
  }
};

/**
 * Rechercher des services traiteur avec filtres avancés
 */
export const searchCateringServices = async (searchParams) => {
  try {
    const queryParams = new URLSearchParams();
    
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] !== undefined && searchParams[key] !== null) {
        queryParams.append(key, searchParams[key]);
      }
    });
    
    const url = `${CATERING_BASE_URL}/services/search?${queryParams.toString()}`;
    const response = await apiCall(url, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la recherche des services traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer un service traiteur par ID
 */
export const getCateringServiceById = async (serviceId) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/services/${serviceId}`, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération du service traiteur:', error);
    throw error;
  }
};

/**
 * Mettre à jour un service traiteur
 */
export const updateCateringService = async (serviceId, serviceData) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du service traiteur:', error);
    throw error;
  }
};

/**
 * Supprimer un service traiteur
 */
export const deleteCateringService = async (serviceId) => {
  try {
    await apiCall(`${CATERING_BASE_URL}/services/${serviceId}`, { method: 'DELETE' });
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression du service traiteur:', error);
    throw error;
  }
};

// =============================================================================
// FORMULES TRAITEUR (plateforme)
// =============================================================================

/**
 * Liste des formules traiteur namke (Buffet / Grande fête / Sur mesure)
 */
export const getCateringFormulas = async () => {
  try {
    return await apiCall(`${CATERING_BASE_URL}/formulas`, { method: 'GET' });
  } catch (error) {
    console.error('Erreur lors de la récupération des formules traiteur:', error);
    throw error;
  }
};

/**
 * Détail d'une formule traiteur par son slug
 */
export const getCateringFormula = async (slug) => {
  try {
    return await apiCall(`${CATERING_BASE_URL}/formulas/${slug}`, { method: 'GET' });
  } catch (error) {
    console.error('Erreur lors de la récupération de la formule traiteur:', error);
    throw error;
  }
};

/**
 * Cuisiniers (restaurants) proposant une formule traiteur donnée
 */
export const getCateringFormulaCooks = async (slug) => {
  try {
    return await apiCall(`${CATERING_BASE_URL}/formulas/${slug}/cooks`, { method: 'GET' });
  } catch (error) {
    console.error('Erreur lors de la récupération des cuisiniers pour cette formule:', error);
    throw error;
  }
};

// =============================================================================
// RÉSERVATIONS TRAITEUR
// =============================================================================

/**
 * Créer une nouvelle réservation traiteur
 */
export const createCateringBooking = async (bookingData) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/bookings`, {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la création de la réservation traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer la liste des réservations traiteur
 */
export const getCateringBookings = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.customer_id) queryParams.append('customer_id', params.customer_id);
    if (params.restaurant_id) queryParams.append('restaurant_id', params.restaurant_id);
    if (params.status_filter) queryParams.append('status_filter', params.status_filter);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `${CATERING_BASE_URL}/bookings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiCall(url, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération des réservations traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer une réservation traiteur par ID
 */
export const getCateringBookingById = async (bookingId) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/bookings/${bookingId}`, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération de la réservation traiteur:', error);
    throw error;
  }
};

/**
 * Mettre à jour une réservation traiteur
 */
export const updateCateringBooking = async (bookingId, bookingData) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/bookings/${bookingId}`, {
      method: 'PUT',
      body: JSON.stringify(bookingData),
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la réservation traiteur:', error);
    throw error;
  }
};

/**
 * Mettre à jour le statut d'une réservation (restaurant uniquement)
 */
export const updateCateringBookingStatus = async (bookingId, status) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/bookings/${bookingId}/status?status=${status}`, {
      method: 'PUT',
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut de la réservation:', error);
    throw error;
  }
};

/**
 * Annuler une réservation traiteur
 */
export const cancelCateringBooking = async (bookingId) => {
  try {
    await apiCall(`${CATERING_BASE_URL}/bookings/${bookingId}`, { method: 'DELETE' });
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'annulation de la réservation traiteur:', error);
    throw error;
  }
};

// =============================================================================
// AVIS TRAITEUR
// =============================================================================

/**
 * Créer un nouvel avis traiteur
 */
export const createCateringReview = async (reviewData) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/reviews`, {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la création de l\'avis traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer la liste des avis traiteur
 */
export const getCateringReviews = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.service_id) queryParams.append('service_id', params.service_id);
    if (params.package_id) queryParams.append('package_id', params.package_id);
    if (params.restaurant_id) queryParams.append('restaurant_id', params.restaurant_id);
    if (params.customer_id) queryParams.append('customer_id', params.customer_id);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `${CATERING_BASE_URL}/reviews${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiCall(url, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération des avis traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer un avis traiteur par ID
 */
export const getCateringReviewById = async (reviewId) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/reviews/${reviewId}`, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'avis traiteur:', error);
    throw error;
  }
};

// =============================================================================
// CALCUL DES PRIX
// =============================================================================

/**
 * Calculer le prix d'un service ou package traiteur
 */
export const calculateCateringPrice = async (priceData) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/calculate-price`, {
      method: 'POST',
      body: JSON.stringify(priceData),
    });
    return response;
  } catch (error) {
    console.error('Erreur lors du calcul du prix traiteur:', error);
    throw error;
  }
};

// =============================================================================
// STATISTIQUES
// =============================================================================

/**
 * Récupérer les statistiques traiteur d'un restaurant
 */
export const getRestaurantCateringStats = async (restaurantId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    
    const url = `${CATERING_BASE_URL}/restaurant/${restaurantId}/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiCall(url, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques traiteur:', error);
    throw error;
  }
};

/**
 * Récupérer l'historique traiteur d'un client
 */
export const getCustomerCateringHistory = async (customerId, params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${CATERING_BASE_URL}/customer/${customerId}/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiCall(url, { method: 'GET' });
    return response;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique traiteur:', error);
    throw error;
  }
};

// === PAIEMENT (DÉPÔT) ===

export const createCateringPaymentIntent = async (bookingId) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/bookings/${bookingId}/create-payment-intent`, {
      method: 'POST'
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la création du payment intent:', error);
    throw error;
  }
};

export const confirmCateringPayment = async (bookingId, paymentIntentId) => {
  try {
    const response = await apiCall(`${CATERING_BASE_URL}/bookings/${bookingId}/confirm-payment`, {
      method: 'POST',
      body: JSON.stringify({ payment_intent_id: paymentIntentId })
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la confirmation du paiement:', error);
    throw error;
  }
};

// =============================================================================
// DONNÉES DE TEST (fallback)
// =============================================================================

// Données de test pour les services traiteur
export const defaultCateringServices = [
  {
    id: 1,
    restaurant_id: 1,
    service_name: "Buffet Mariage Premium",
    service_description: "Buffet complet pour mariage avec service professionnel",
    service_type: "mariage",
    min_guests: 50,
    max_guests: 200,
    base_price: 1500.00,
    price_per_person: 25.00,
    delivery_available: true,
    delivery_fee: 100.00,
    setup_available: true,
    setup_fee: 200.00,
    staff_available: true,
    staff_fee_per_hour: 50.00,
    rating: 4.8,
    review_count: 12,
    is_active: true,
    featured: true
  },
  {
    id: 2,
    restaurant_id: 1,
    service_name: "Cocktail Entreprise",
    service_description: "Service cocktail pour événements d'entreprise",
    service_type: "entreprise",
    min_guests: 20,
    max_guests: 100,
    base_price: 800.00,
    price_per_person: 15.00,
    delivery_available: true,
    delivery_fee: 80.00,
    setup_available: false,
    setup_fee: 0.00,
    staff_available: true,
    staff_fee_per_hour: 40.00,
    rating: 4.5,
    review_count: 8,
    is_active: true,
    featured: false
  }
];

// Données de test pour les packages traiteur
export const defaultCateringPackages = [
  {
    id: 1,
    restaurant_id: 1,
    package_name: "Package Mariage Complet",
    package_description: "Package tout inclus pour mariage",
    package_type: "premium",
    base_price: 2000.00,
    price_per_person: 30.00,
    min_guests: 50,
    max_guests: 200,
    includes_drinks: true,
    includes_dessert: true,
    includes_service: true,
    includes_setup: true,
    includes_equipment: true,
    is_active: true,
    featured: true
  },
  {
    id: 2,
    restaurant_id: 1,
    package_name: "Package Standard",
    package_description: "Package de base pour événements",
    package_type: "standard",
    base_price: 1000.00,
    price_per_person: 20.00,
    min_guests: 20,
    max_guests: 100,
    includes_drinks: false,
    includes_dessert: true,
    includes_service: false,
    includes_setup: false,
    includes_equipment: false,
    is_active: true,
    featured: false
  }
];

// Données de test pour les réservations traiteur
export const defaultCateringBookings = [
  {
    id: 1,
    customer_id: 1,
    restaurant_id: 1,
    service_id: 1,
    package_id: null,
    event_date: "2024-12-25",
    event_time: "18:00:00",
    guest_count: 100,
    total_amount: 5000.00,
    status: "confirmed",
    payment_status: "paid",
    deposit_amount: 1500.00,
    deposit_paid: true,
    created_at: "2024-11-01T10:00:00Z"
  },
  {
    id: 2,
    customer_id: 2,
    restaurant_id: 1,
    service_id: 2,
    package_id: null,
    event_date: "2024-12-30",
    event_time: "19:00:00",
    guest_count: 50,
    total_amount: 2500.00,
    status: "pending",
    payment_status: "pending",
    deposit_amount: 750.00,
    deposit_paid: false,
    created_at: "2024-11-05T14:30:00Z"
  }
];

// Données de test pour les avis traiteur
export const defaultCateringReviews = [
  {
    id: 1,
    booking_id: 1,
    customer_id: 1,
    restaurant_id: 1,
    service_id: 1,
    package_id: null,
    rating: 5,
    review_text: "Service exceptionnel, tout était parfait !",
    food_quality_rating: 5,
    service_quality_rating: 5,
    value_for_money_rating: 4,
    overall_experience_rating: 5,
    is_verified: true,
    created_at: "2024-12-26T10:00:00Z"
  },
  {
    id: 2,
    booking_id: 2,
    customer_id: 2,
    restaurant_id: 1,
    service_id: 2,
    package_id: null,
    rating: 4,
    review_text: "Très bon service, personnel professionnel",
    food_quality_rating: 4,
    service_quality_rating: 5,
    value_for_money_rating: 4,
    overall_experience_rating: 4,
    is_verified: true,
    created_at: "2024-12-31T15:30:00Z"
  }
];

// Export de l'objet principal
export const cateringApi = {
  // Formules (plateforme)
  getCateringFormulas,
  getCateringFormula,
  getCateringFormulaCooks,

  // Services
  createCateringService,
  getCateringServices,
  searchCateringServices,
  getCateringServiceById,
  updateCateringService,
  deleteCateringService,

  // Réservations
  createCateringBooking,
  getCateringBookings,
  getCateringBookingById,
  updateCateringBooking,
  updateCateringBookingStatus,
  cancelCateringBooking,
  
  // Avis
  createCateringReview,
  getCateringReviews,
  getCateringReviewById,
  
  // Calcul des prix
  calculateCateringPrice,
  
  // Statistiques
  getRestaurantCateringStats,
  getCustomerCateringHistory,

  // Paiement
  createCateringPaymentIntent,
  confirmCateringPayment,
  
  // Données de test
  defaultCateringServices,
  defaultCateringPackages,
  defaultCateringBookings,
  defaultCateringReviews
};
